/**
 * Print Weeks chapter — Canva page-12 “What to do each week”.
 * Own A4 sheet: 2×2 week cards with colored headers + FOCUS line,
 * check-in questions, tip, optional personal note.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  Check,
  Lightbulb,
  Settings,
  Sparkles,
  Target,
  AlertTriangle,
} from "lucide-react";
import type {
  MonthlyConsultationBundle,
  WeekPlanRow,
} from "../../../../utils/forecast/monthlyConsultation";
import weekCornerChart from "../../../../assets/monthly-consultation/pdf/week-corner-chart.svg";
import weekCornerClipboard from "../../../../assets/monthly-consultation/pdf/week-corner-clipboard.svg";
import weekCornerEnvelope from "../../../../assets/monthly-consultation/pdf/week-corner-envelope.svg";
import weekCornerMountains from "../../../../assets/monthly-consultation/pdf/week-corner-mountains.svg";
import {
  InlineSvgWeekQuality,
  weekQualityIconKind,
} from "../../shared/chapterIcons";
import { PdfChapterShell, PdfCornerLuopan } from "../atoms";
import {
  clipPdfText,
  stripPrintDashes,
  WEEK_CALENDAR_TIP,
  weekCalendarIntro,
  weekCheckInQuestions,
  weekHeaderStanceLabel,
  weekStanceFromQuality,
} from "../pdfCopy";
import type { WeekTeaserStance } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  green,
  mutedGray,
  paleTan,
  steadyBlue,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF weeks chapter.
 */
export interface PdfChapterWeeksProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Soft cap for week body copy. */
const WEEK_BODY_CLIP = 110;
/** Soft cap for the personal-note banner. */
const NOTE_CLIP = 180;
/** Empty-state body when a week slot has no live `useFor`. */
const WEEK_EMPTY = "No week plan was recorded for this slot.";

/**
 * Print model for one Canva-style week card.
 */
interface WeekCardModel {
  /** 1-based week index. */
  readonly index: number;
  /** Window label (e.g. Week 1 (days 1 to 7)). */
  readonly windowLabel: string;
  /** Colored-header stance (`BEST TO PUSH`). */
  readonly headerLabel: string;
  /** Push / Careful / Steady / Rest. */
  readonly stance: WeekTeaserStance;
  /** Header bar fill. */
  readonly headerColor: string;
  /** Body coaching sentence. */
  readonly body: string;
  /** FOCUS keyword line. */
  readonly focus: string;
}

/**
 * Thin gold rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Header-bar color for a week stance.
 *
 * @param stance - Push / Careful / Steady / Rest
 * @returns Theme hex
 */
function stanceHeaderColor(stance: WeekTeaserStance): string {
  if (stance === "Push") {
    return green;
  }
  if (stance === "Careful") {
    return tanGold;
  }
  if (stance === "Steady") {
    return steadyBlue;
  }
  return mutedGray;
}

/**
 * Canva FOCUS keywords by week slot (page-12 uses week index, not stance).
 *
 * @param weekIndex - 1-based week index
 * @returns Keyword line
 */
function weekSlotFocusKeywords(weekIndex: number): string {
  if (weekIndex === 2) {
    return "Patience · Restraint · Review";
  }
  if (weekIndex === 3) {
    return "Follow-through · Communication · Systems";
  }
  if (weekIndex === 4) {
    return "Completion · Closure · Momentum";
  }
  return "Clarity · Boundaries · Direction";
}

/**
 * FOCUS-row icon by week slot (Canva: target / warn / gear / check).
 *
 * @param weekIndex - 1-based week index
 * @returns Lucide icon
 */
function weekSlotFocusIcon(weekIndex: number): LucideIcon {
  if (weekIndex === 2) {
    return AlertTriangle;
  }
  if (weekIndex === 3) {
    return Settings;
  }
  if (weekIndex === 4) {
    return Check;
  }
  return Target;
}

/**
 * Build four week cards from live `weekPlan`, padding empty slots.
 *
 * @param rows - Bundle week plan
 * @returns Exactly four cards
 */
function weekCards(rows: readonly WeekPlanRow[]): WeekCardModel[] {
  const live = Array.isArray(rows) ? rows : [];
  const cards: WeekCardModel[] = [];
  for (let index = 0; index < 4; index += 1) {
    const row = index < live.length ? live[index] : undefined;
    const quality =
      row !== undefined && typeof row.quality === "string" ? row.quality : "";
    const stance = weekStanceFromQuality(quality);
    const windowRaw =
      row !== undefined && typeof row.window === "string"
        ? row.window.trim()
        : "";
    const useFor =
      row !== undefined && typeof row.useFor === "string"
        ? row.useFor.trim()
        : "";
    const weekIndex = index + 1;
    cards.push({
      index: weekIndex,
      windowLabel:
        windowRaw.length > 0 ? windowRaw : `Week ${String(weekIndex)}`,
      headerLabel: weekHeaderStanceLabel(quality),
      stance,
      headerColor: stanceHeaderColor(stance),
      body: clipPdfText(useFor.length > 0 ? useFor : WEEK_EMPTY, WEEK_BODY_CLIP),
      focus: weekSlotFocusKeywords(weekIndex),
    });
  }
  return cards;
}

/**
 * Resolve a webpack URL for a week-card corner watermark SVG.
 *
 * @param moduleSrc - Imported SVG/PNG module
 * @returns Trimmed URL, or empty
 */
function assetSrc(moduleSrc: unknown): string {
  return typeof moduleSrc === "string" ? moduleSrc.trim() : "";
}

/**
 * Canva corner watermark for week index 1–4 (mountains / clipboard / envelope / chart).
 *
 * @param weekIndex - 1-based week index
 * @returns Image URL, or empty when unresolved
 */
function weekCornerSrc(weekIndex: number): string {
  if (weekIndex === 1) {
    return assetSrc(weekCornerMountains);
  }
  if (weekIndex === 2) {
    return assetSrc(weekCornerClipboard);
  }
  if (weekIndex === 3) {
    return assetSrc(weekCornerEnvelope);
  }
  return assetSrc(weekCornerChart);
}

/**
 * One Canva-style week card: prominent header, compact body, FOCUS + corner art.
 *
 * @param props - Card model
 * @returns Week card
 */
const PdfCalendarWeekCard: React.FC<{ readonly card: WeekCardModel }> = ({
  card,
}) => {
  const iconKind = weekQualityIconKind(card.headerLabel);
  const FocusIcon = weekSlotFocusIcon(card.index);
  const corner = weekCornerSrc(card.index);

  return (
    <div
      className="mc-pdf-card"
      style={{
        border: `1px solid ${border}`,
        borderRadius: 14,
        overflow: "hidden",
        background: cardWhite,
        fontFamily: bodyFontFamily,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxShadow: "0 1px 3px rgba(26,30,63,0.06)",
      }}
    >
      <div
        style={{
          minHeight: 48,
          padding: "12px 14px",
          background: card.headerColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: cardWhite,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.24)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <InlineSvgWeekQuality kind={iconKind} size={18} />
          </span>
          {`W${String(card.index)}`}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: cardWhite,
          }}
        >
          {card.headerLabel}
        </span>
      </div>

      <div
        style={{
          padding: "10px 14px 8px",
          position: "relative",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: displayFontFamily,
            fontSize: 13,
            fontWeight: 700,
            color: terracotta,
            lineHeight: 1.25,
          }}
        >
          {card.windowLabel}
        </p>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 12,
            lineHeight: 1.4,
            color: charcoal,
            paddingRight: 28,
          }}
        >
          {card.body}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px 10px",
          borderTop: `1px dashed ${border}`,
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: `${card.headerColor}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FocusIcon size={16} color={card.headerColor} strokeWidth={2} />
        </span>
        <div style={{ minWidth: 0, paddingRight: 56 }}>
          <p
            style={{
              margin: 0,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: terracotta,
              lineHeight: 1.2,
            }}
          >
            Focus
          </p>
          <p
            style={{
              margin: "2px 0 0",
              fontSize: 11,
              fontWeight: 600,
              color: charcoal,
              lineHeight: 1.3,
            }}
          >
            {card.focus}
          </p>
        </div>
      </div>

      {corner.length > 0 ? (
        <img
          src={corner}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 2,
            bottom: 2,
            width: 78,
            height: 64,
            objectFit: "contain",
            objectPosition: "right bottom",
            opacity: 0.42,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ) : null}
    </div>
  );
};

/**
 * What to do each week — one A4 sheet of 2×2 calendar cards.
 */
export const PdfChapterWeeks: React.FC<PdfChapterWeeksProps> = ({
  bundle,
  pageNumber,
}) => {
  const profileName =
    typeof bundle.profileName === "string" ? bundle.profileName.trim() : "";
  const intro = weekCalendarIntro(profileName);
  const plan = Array.isArray(bundle.weekPlan) ? bundle.weekPlan : [];
  const cards = weekCards(plan);

  const palaceName =
    typeof bundle.stack.liuYueLifePalaceNameEnglish === "string"
      ? bundle.stack.liuYueLifePalaceNameEnglish
      : "";
  const questions = weekCheckInQuestions({
    primaryMove:
      typeof bundle.monthContract.primaryMove === "string"
        ? bundle.monthContract.primaryMove
        : "",
    trapName:
      typeof bundle.failureMode.name === "string" ? bundle.failureMode.name : "",
    palaceName,
  });

  const letterPara =
    Array.isArray(bundle.letter.bodyParagraphs) &&
    typeof bundle.letter.bodyParagraphs[0] === "string"
      ? bundle.letter.bodyParagraphs[0].trim()
      : "";
  const identityLine =
    typeof bundle.archetype.identityLine === "string"
      ? bundle.archetype.identityLine.trim()
      : "";
  const noteBody = clipPdfText(
    letterPara.length > 0 ? letterPara : identityLine,
    NOTE_CLIP
  );
  const noteTitle =
    profileName.length > 0
      ? `Personal note for ${profileName}`
      : "Personal note";
  const noteSignParts: string[] = [];
  if (profileName.length > 0) {
    noteSignParts.push(profileName);
  }
  noteSignParts.push(`M${String(bundle.lunarMonth)} ${String(bundle.solarYear)}`);
  const noteSign = noteSignParts.join(" · ");

  return (
    <PdfChapterShell
      chapterId="weeks"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      fillPage={true}
      compact
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 40,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
        }}
      >
        <div className="mc-pdf-corner-luopan-band" style={{ flexShrink: 0 }}>
          <PdfCornerLuopan />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
              position: "relative",
              zIndex: 1,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `${tanGold}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Calendar size={18} color={terracotta} strokeWidth={1.8} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                + CALENDAR
              </p>
              <h2
                style={{
                  margin: "2px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  color: charcoal,
                }}
              >
                What to do each week
              </h2>
            </div>
          </div>
          <p
            style={{
              margin: "0 0 8px",
              position: "relative",
              zIndex: 1,
              fontSize: 12,
              lineHeight: 1.45,
              color: mutedGray,
            }}
          >
            {intro}
          </p>
          <div style={{ position: "relative", zIndex: 1 }}>
            <PdfDiamondRule color={tanGold} />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            flexShrink: 0,
          }}
        >
          {cards.map((card) => (
            <PdfCalendarWeekCard
              key={`week-${String(card.index)}`}
              card={card}
            />
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.35fr 1fr",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            className="mc-pdf-card"
            style={{
              background: cardWhite,
              border: `1px solid ${border}`,
              borderRadius: 14,
              padding: "12px 14px",
              fontFamily: bodyFontFamily,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <Sparkles size={14} color={terracotta} strokeWidth={2} aria-hidden="true" />
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                Weekly check-in questions
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {questions.map((question, index) => (
                <div
                  key={`checkin-${String(index)}`}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: terracotta,
                      color: cardWhite,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      marginTop: 1,
                    }}
                  >
                    {String(index + 1)}
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: charcoal,
                    }}
                  >
                    {stripPrintDashes(question)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mc-pdf-card"
            style={{
              background: cardWhite,
              border: `1px solid ${border}`,
              borderRadius: 14,
              padding: "12px 14px",
              fontFamily: bodyFontFamily,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: paleTan,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Lightbulb size={24} color={terracotta} strokeWidth={1.8} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                Tip
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: charcoal,
                }}
              >
                {WEEK_CALENDAR_TIP}
              </p>
            </div>
          </div>
        </div>

        {noteBody.length > 0 ? (
          <div
            className="mc-pdf-card"
            style={{
              background: cream,
              border: `1px solid ${border}`,
              borderRadius: 14,
              padding: "14px 16px",
              fontFamily: bodyFontFamily,
              display: "grid",
              gridTemplateColumns: "1.7fr auto 1fr",
              gap: 14,
              alignItems: "stretch",
              boxSizing: "border-box",
              flexShrink: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 10,
                top: 2,
                fontFamily: displayFontFamily,
                fontSize: 64,
                lineHeight: 1,
                color: terracotta,
                opacity: 0.18,
                pointerEvents: "none",
              }}
            >
              “
            </span>
            <div style={{ minWidth: 0, position: "relative", zIndex: 1, paddingLeft: 8 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                {noteTitle}
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: charcoal,
                }}
              >
                {stripPrintDashes(noteBody)}
              </p>
            </div>
            <span
              aria-hidden="true"
              style={{
                width: 1,
                background: border,
                alignSelf: "stretch",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              {noteSign.length > 0 ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: terracotta,
                    lineHeight: 1.3,
                  }}
                >
                  {noteSign}
                </p>
              ) : null}
              <p
                style={{
                  margin: 0,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: mutedGray,
                  lineHeight: 1.3,
                }}
              >
                Consultation brief complete
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterWeeks;
